import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
// No types, typescript throws an error
// @ts-ignore
import html2pdf from 'html2pdf.js';
import AccountFilterTable, { ISelectedAccount } from 'views/common/AccountFilterTable';
import { makeStyles } from '@material-ui/core';
import { BreadcrumbsItem } from 'react-breadcrumbs-dynamic';
import { dynamicTranslate, translate } from 'components/Translation';
import { AccountsService } from 'services/accounts/accounts';
import { finalize } from 'rxjs/operators';
import { useDispatch } from 'react-redux';
import { hideLoadingIndicator, showLoadingIndicator } from 'redux/modules/api/loadingIndicator/actions';
import PaymentService from 'services/payments/payment';
import moment from 'moment';
import numeral from 'numeral';
import Business from '@material-ui/icons/Business';
import styles from 'assets/jss/material-dashboard-pro-react/views/payments/historyStyle';
import PDF from 'assets/img/icons/pdf-outline.svg';
import { AccountCell, PhoneNumberCell, TextCell, UserNameCell } from 'components/Cells/Cells';
import CustomAvatar from 'components/CustomAvatar/CustomAvatar';
import { exportToXLS } from 'utils/export/export';
import { BodyM, BodyMBold, BodySBold, CaptionLight, HeadingMBold } from 'components/Typography/Typography';
import { SingleCorpAccount } from 'utils/api/models/accounts/singleCorpAccount';
import { FilterValue } from 'react-table';
import { PaymentDetails } from 'utils/api/models/payments/paymentDetails';
import PaymentDetailsCard from 'views/PaymentsHistory/subcomponents/PaymentDetailsCard';
import Button from 'components/CustomButtons/Button';
import { BillingDateSelector } from 'views/Bills/BillsList/subcomponents/Filters/BillingDateSelector';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import CustomRadio from 'components/CustomRadio/CustomRadio';
import { ALL, ExtractKey, LAST_DAYS_FILTER, PERIOD_FILTER } from './constants';
import { useSelector } from 'react-redux';
import { getCommonLabels, getInvoiceLabels } from 'redux/modules/dynamicLabels/selectors';
import { setDataLayer } from 'commons/gtm/dataLayerWrapper';
import { getLanguage } from 'utils/language/language';
import { upIdSelector } from 'redux/modules/session/selectors';
import { AlternativeSelectWrapper } from 'views/common/AlternativeSelectWrapper';
import { RequestType, SinglePaymentHistory } from 'utils/api/models/payments/paymentsHistoryData';
import { PaymentsHistoryType } from 'utils/api/models/payments/paymentHistory';
import { CellWithTooltip } from 'components/CellWithTooltip';
import { remoteConfigSelector } from 'redux/modules/settings/remoteConfig/selectors';
import { Payments } from 'utils/api/models/payments/payments';
import { SinglePayment } from 'utils/api/models/payments/singlePayment';
import { isBOMOn } from 'utils/killswitch';
import { DATE_TIME } from 'views/Bills/constants';
import { roundup } from 'components/PaymentMethod/helper/number';
import { decodeBase64 } from 'views/PaymentRequestStatusBOM/util';
import useNewTheme from 'commons/hooks/newtheme/useNewTheme';

// @ts-ignore
const useStyles = makeStyles(styles);

const LAST_DAYS_NUMBER = 30;

interface Period {
	startDate: moment.Moment;
	endDate: moment.Moment;
}

const PaymentsHistory = () => {
	const invoiceListLabels = useSelector(getInvoiceLabels) || {};
	const [loading, setLoading] = useState({ accounts: false, payments: false, paymentDetails: false });
	const [accounts, setAccounts] = useState<SingleCorpAccount[]>([]);
	const [selectedAccount, setSelectedAccount] = useState<ISelectedAccount | null>(null);
	const [selectedPayments, setSelectedPayments] = useState<never[]>([]);
	const [searchValue, setSearchValue] = useState<string>('');
	const [payments, setPayments] = useState<SinglePaymentHistory[]>([]);
	const [filteredPayments, setFilteredPayments] = useState<SinglePaymentHistory[]>([]);
	const [filters, setFilters] = useState<FilterValue>({ period: [LAST_DAYS_FILTER] });
	const [paymentDetails, setPaymentDetails] = useState<PaymentDetails | null>(null);
	// kill switch to check if Bank Muscat are to be shown
	const remoteConfig = useSelector(remoteConfigSelector);
	const commonLabels = useSelector(getCommonLabels);
	const isBankMuscatEnabled = isBOMOn(remoteConfig);
	const [period, setPeriod] = useState<Period>({
		startDate: moment()
			.subtract(LAST_DAYS_NUMBER, 'd')
			.startOf('day'),
		endDate: moment().endOf('day'),
	});
	const dispatch = useDispatch();
	const isCorporate = useMemo(() => accounts.length === 1, [accounts]);
	const isNewHeaderVisible = useNewTheme();
	const classes = useStyles({ isNewHeaderVisible });
	const isMounted = useRef(true);
	const [showAlternatiiveSelectChild, setShowAlternatiiveSelectChild] = useState(false);

	const [dates, setDates] = useState<Period>({
		startDate: moment()
			.subtract(invoiceListLabels.searchingLimit, 'months')
			.startOf('day'),
		endDate: moment().endOf('day'),
	});
	const [startDateSelected, setStartDateSelected] = useState(false);
	const [endDateSelected, setEndDateSelected] = useState(false);
	const [openDatePicker, setOpenDatePicker] = useState(false);
	const [altSelValue, setAltSelValue] = useState(invoiceListLabels?.last30days);
	const [selectedRadioButton, setSelectedRadioButton] = useState({ period: '' });
	const accId = useSelector(upIdSelector);

	const handleChange = (event: any) => {
		const { name, value } = event.target;
		setSelectedRadioButton((prevState) => ({
			...prevState,
			[name]: value,
		}));
		setFilters((filters: FilterValue) => ({ ...filters, [name]: [value] }));
	};
	useEffect(() => {
		const filterData = (arr: any[]) => {
			return arr?.filter(Boolean).some((value: string | number) =>
				value
					.toString()
					.toLowerCase()
					.includes(searchValue.toLowerCase())
			);
		};
		setFilteredPayments(() => {
			if (searchValue !== '') {
				return payments.filter((payment) => {
					const accId = [payment.accountId];
					const billingNumber = payment.billNumberList?.map((key) => key.billingId);

					return filterData(accId) || filterData(billingNumber);
				});
			} else {
				return payments;
			}
		});
		setSelectedPayments([]);
	}, [payments, searchValue]);

	const assignAccountsToPayment = useCallback(
		(payments) => {
			setPayments(() => {
				const accountsMap = accounts.reduce((acc, account) => ({ ...acc, [account.accountId]: account }), {});
				payments.sort(function(a: SinglePaymentHistory, b: SinglePaymentHistory) {
					return new Date(b.paymentDate).getTime() - new Date(a.paymentDate).getTime();
				});
				// Map payments to include account information with error handling
				return payments.map((payment: SinglePaymentHistory) => {
					const accountId = isBankMuscatEnabled ? payment?.billNumberList?.[0]?.accountId : payment.accountId;
					const account = accountId ? accountsMap[accountId] : null;
					return {
						...payment,
						account: account ?? null, // Use nullish coalescing to ensure account is always an object or null
					};
				});
			});
		},
		[accounts]
	);
	const getPaymentReceipt = (paymentId: string, date: string) => {
		dispatch(showLoadingIndicator());
		PaymentService.getPaymentReceipt(paymentId)
			.pipe(finalize(() => dispatch(hideLoadingIndicator())))
			.subscribe(({ response }) => {
				if (isMounted.current) {
					let data = response?.getPaymentReceiptResponse?.htmlReceiptBase64;
					if (data) {
						const encodedData = decodeBase64(data);
						html2pdf()
							.from(encodedData)
							.set({
								filename: `payment${date}.pdf`,
								margin: 0,
								jsPDF: {
									unit: 'mm',
									format: 'a4',
									orientation: 'portrait',
								},
								html2canvas: {
									height: 1080,
									useCORS: true,
								},
								enableLinks: true,
							})
							.save();
					}
				}
			});
	};

	useEffect(() => {
		setLoading((loading) => ({ ...loading, accounts: true }));
		AccountsService.getCorpAccountsForCurrentUser()
			.pipe(
				finalize(() => {
					if (isMounted.current) {
						setLoading((loading) => ({ ...loading, accounts: false }));
					}
				})
			)
			.subscribe(({ accounts }) => isMounted.current && setAccounts(accounts));
	}, [dispatch]);

	useEffect(() => {
		setLoading((loading) => ({ ...loading, payments: true }));
		const [fromDate, toDate] = ((period, { startDate, endDate }) => {
			switch (period[0]) {
				case LAST_DAYS_FILTER:
					return [
						moment()
							.subtract(LAST_DAYS_NUMBER, 'd')
							.startOf('day'),
						moment().endOf('day'),
					];

				case ALL:
					return [undefined, undefined];

				case PERIOD_FILTER:
					return [dates.startDate, dates.endDate];

				default:
					return [moment(startDate), moment(endDate)];
			}
		})(filters.period, period);
		if (isBankMuscatEnabled) {
			const dateFormat = 'YYYY-MM-DD';
			PaymentService.fetchPaymentHistory({
				//corpAccountId: selectedAccount?.id,
				fromDate: fromDate?.format(dateFormat),
				toDate: toDate?.format(dateFormat),
			}).subscribe(({ response }) => {
				setLoading((loading) => ({ ...loading, payments: false }));
				const data: PaymentsHistoryType = response;
				const paymentsData: SinglePaymentHistory[] = data.paymentHistory;
				assignAccountsToPayment(paymentsData);
			});
		} else {
			const dateFormat = 'YYYY-MM-DDTHH:mm:ss';

			PaymentService.getPaymentsHistoryForAccounts({
				corpAccountId: selectedAccount?.id,
				fromDate: fromDate?.format(dateFormat),
				toDate: toDate?.format(dateFormat),
			})
				.pipe(finalize(() => setLoading((loading) => ({ ...loading, payments: false }))))
				.subscribe(({ response }: Payments) => {
					assignAccountsToPayment(response.getPaymentsHistoryForAccountsResponse.paymentsData);
				});
		}
	}, [selectedAccount, filters.period, period, assignAccountsToPayment, dates]);

	useEffect(() => {
		if (Object.keys(loading).some((key) => loading[key])) {
			dispatch(showLoadingIndicator());
		} else {
			dispatch(hideLoadingIndicator());
		}
	}, [loading, dispatch]);

	useEffect(() => {
		return () => {
			isMounted.current = false;
		};
	}, []);

	const getPaymentDetails = (paymentId: string) => {
		setLoading((loading) => ({ ...loading, paymentDetails: true }));
		PaymentService.getPaymentHistoryRowItems(paymentId)
			.pipe(finalize(() => setLoading((loading) => ({ ...loading, paymentDetails: false }))))
			.subscribe((data) => setPaymentDetails(data));
	};

	const accountsMap = useMemo(
		() => accounts.reduce((acc, account) => ({ ...acc, [account.accountId]: account }), {}),
		[accounts]
	);

	const exportToXls = useCallback(() => {
		setDataLayer('download', getLanguage(), accId, '', null, {
			type: 'Receipt',
			quantity: selectedPayments.length,
		});
		const preparedData = (selectedPayments.length === 0
			? payments
			: selectedPayments.map((index) => payments[index])
		).map((payment) => ({
			Date: payment.paymentDate,
			Type: translate(`payments.history.rowType.${payment.paymentRequestType}`),
			['Bill No.']: payment.billNumberList.map((bills) => `${bills.billNumber}`).join(','),
			Organization: payment?.account?.accountName || '',
			['Payment Method ']: invoiceListLabels[payment.paymentMethod],
			Amount: dynamicTranslate(commonLabels.price, roundup(payment?.amount?.value || 0)),
		}));
		exportToXLS(preparedData, `paymentHistory ${moment().format('LLL')}`);
	}, [selectedPayments, accountsMap, payments]);

	// const validateDateString = (date: string): boolean => {
	// 	return /^([0][1-9]|[12][0-9]|3[01])[/](0?[1-9]|1[012])[/]\d{4}$/.test(date);
	// };

	// const dateChanged = (date: string | moment.Moment, key: 'startDate' | 'endDate'): void => {
	// 	if (typeof date === 'string') {
	// 		if (validateDateString(date)) {
	// 			const updatedDate = moment(date);
	// 			if (updatedDate.isValid()) {
	// 				setPeriod((period) => ({ ...period, [key]: updatedDate }));
	// 			}
	// 		}
	// 	} else {
	// 		setPeriod((period) => ({ ...period, [key]: date }));
	// 	}
	// };
	const renderFilter = () => (
		<div className={classes.filtersWrapper}>
			<div className={classes.alternativeSelectWrapper}>
				<div>
					<AlternativeSelectWrapper
						showBorder
						dropdownIcon={<ArrowDropDownIcon />}
						renderChildren={showAlternatiiveSelectChild}
						setRenderChildren={setShowAlternatiiveSelectChild}
						type="sm"
						id="period-select"
						label={invoiceListLabels.billingPeriodFilter}
						value={<div>{altSelValue}</div>}
						onClick={() => {
							setShowAlternatiiveSelectChild(true);
						}}
						items={[]}
						showItems={false}
						selectedItems={[]}
						withHorizontalPadding
					>
						<div className={classes.periodsWrapper}>
							<div className={classes.period}>
								<CustomRadio
									checked={selectedRadioButton.period === LAST_DAYS_FILTER}
									//@ts-ignore
									onChange={(e) => {
										handleChange(e);
										setAltSelValue(invoiceListLabels?.last30days);
									}}
									value="lastDays"
									name="period"
									label="Last 30 days"
								/>
							</div>
							<div className={classes.period}>
								<CustomRadio
									checked={selectedRadioButton.period === PERIOD_FILTER}
									//@ts-ignore
									onChange={(e) => handleChange(e)}
									value="period"
									name="period"
									label="Custom Date Range"
								/>
							</div>
							{selectedRadioButton.period === PERIOD_FILTER && (
								<div className={classes.billPeriodWrapper}>
									<BodySBold>
										{dynamicTranslate(
											invoiceListLabels.selectUpto,
											invoiceListLabels.searchingLimit
										)}
									</BodySBold>
									<BillingDateSelector
										dates={dates}
										setDates={setDates}
										startDateSelected={startDateSelected}
										endDateSelected={endDateSelected}
										setEndDateSelected={setEndDateSelected}
										setStartDateSelected={setStartDateSelected}
										openDatePicker={openDatePicker}
										setOpenDatePicker={setOpenDatePicker}
										setAltSelValue={setAltSelValue}
									/>
									<CaptionLight customClassName={classes.caption}>
										{dynamicTranslate(
											invoiceListLabels.invoicePeriodNote,
											invoiceListLabels.searchingLimit
										)}
									</CaptionLight>
								</div>
							)}
						</div>
					</AlternativeSelectWrapper>
				</div>
			</div>
		</div>
	);

	//Columns for feature toggle, if BOM API is not enabled, will show these columns
	const featureToggleColumns = [
		{
			Header: translate('date'),
			accessor: 'date',
			width: 200,
			Cell: ({ original }: { original: SinglePaymentHistory }) => (
				<TextCell text={moment(original.paymentDate).format('hh:mm A DD.MM.YYYY')} />
			),
		},
		{
			Header: translate('type'),
			accessor: 'type',
			width: 150,
			Cell: ({ original }: { original: SinglePaymentHistory }) => {
				const text = translate(`payments.history.rowType.${original.paymentRequestType}`);
				return original.paymentRequestType === 'GROUP_TOP_UP' ? (
					<div onClick={() => getPaymentDetails(original.paymentRequestId)} className={classes.type}>
						<BodyMBold color="brand" disableMargin>
							{text}
						</BodyMBold>
					</div>
				) : (
					<TextCell text={text} />
				);
			},
		},
		!isCorporate && {
			Header: translate('organization.name'),
			accessor: 'account.accountName',
			Cell: ({ original }: { original: any }) =>
				original.account ? (
					<AccountCell
						text={original.account.accountName}
						icon={
							<CustomAvatar
								placeholderIcon={<Business />}
								accountId={original.account.accountId}
								customClasses={classes.inlineIcon}
								alt="avatar"
							/>
						}
					/>
				) : null,
		},
		{
			Header: translate('phoneNumber'),
			accessor: 'phoneNumber',
			Cell: ({ original }: { original: SinglePaymentHistory }) =>
				original.phoneNumber ? <PhoneNumberCell text={original.phoneNumber} /> : null,
		},
		{
			Header: translate('user.name'),
			accessor: 'username',
			Cell: ({ original }: { original: SinglePaymentHistory }) =>
				original.username ? <UserNameCell text={original.username} /> : null,
		},
		{
			Header: translate('documents'),
			id: 'documents',
			width: 110,
			Cell: ({ original }: { original: SinglePaymentHistory }) => (
				<div
					id={`${original.paymentRequestId}-download-button`}
					className={classes.document}
					onClick={() => getPaymentReceipt(original.paymentRequestId, original.paymentDate)}
				>
					<img src={PDF} alt="pdf" />
				</div>
			),
		},
		{
			Header: translate('amount'),
			accessor: 'amount',
			Cell: ({ original }: { original: SinglePayment }) => (
				<div className={classes.amountCell}>
					<BodyMBold inline>{numeral(original.amount).format('0,0.000')}</BodyMBold>{' '}
					<BodyM inline>{translate('OMR')}</BodyM>
				</div>
			),
		},
	].filter(Boolean) as Array<any>;

	return (
		<div className={classes.tableWrapper}>
			<HeadingMBold color="secondary-light" disableMargin data-testId="invoice">
				{invoiceListLabels.heading}
			</HeadingMBold>
			{renderFilter()}
			<AccountFilterTable
				loading={Object.keys(loading).every((key) => loading[key])}
				showTableAdditionalElement={!loading.paymentDetails && !!paymentDetails}
				tableAdditionalElement={
					<div className={classes.tableAdditionalElement}>
						<PaymentDetailsCard
							paymentDetails={paymentDetails}
							isCorporate={isCorporate}
							closePaymentDetails={() => setPaymentDetails(null)}
							accountsMap={accountsMap}
						/>
					</div>
				}
				showPaginationBottom={true}
				showPaginationTop={false}
				accounts={accounts}
				setAccount={setSelectedAccount}
				selectedAccount={selectedAccount}
				filtersValue={filters}
				filters={[]}
				hideOrgFilter
				onFilterChange={(name, value) =>
					setFilters((filters: FilterValue) => {
						return { ...filters, [name]: [value] };
					})
				}
				data={filteredPayments}
				onSelect={setSelectedPayments}
				selectPanel={{}}
				selected={selectedPayments}
				columns={
					isBankMuscatEnabled
						? ([
								{
									Header: translate('table.date.time'),
									accessor: 'paymentDate',
									Cell: ({ original }: { original: SinglePaymentHistory }) => {
										return <TextCell text={moment(original.paymentDate).format(DATE_TIME)} />;
										// return <TextCell text={moment(original.paymentDate).format('LL, HH:MM')} />;
									},
								},
								{
									Header: translate('type'),
									accessor: 'type',
									Cell: ({ original }: { original: SinglePaymentHistory }) => {
										const text = translate(
											`payments.history.rowType.${original.paymentRequestType}`
										);
										return original.paymentRequestType === RequestType.GROUP_TOP_UP ? (
											<div
												onClick={() => getPaymentDetails(original.paymentRequestId)}
												className={classes.type}
											>
												<BodyMBold color="brand" disableMargin>
													{text}
												</BodyMBold>
											</div>
										) : (
											<TextCell text={text} />
										);
									},
								},
								{
									Header: translate('organization.name'),
									accessor: 'account.accountName',
									Cell: ({ original }: { original: any }) =>
										original.account ? (
											<AccountCell
												text={original.account.accountName}
												icon={
													<CustomAvatar
														placeholderIcon={<Business />}
														accountId={original.account.accountId}
														customClasses={classes.inlineIcon}
														alt="avatar"
													/>
												}
											/>
										) : null,
								},
								{
									Header: invoiceListLabels.billNo,
									accessor: 'billingId',
									sortable: false,
									Cell: ({ original }: { original: SinglePaymentHistory }) => (
										<CellWithTooltip
											list={original.billNumberList}
											extractKey={ExtractKey.BILL_NUMBER}
										/>
									),
								},
								// {
								// 	Header: invoiceListLabels.accountId,
								// 	accessor: 'accountId',
								// 	sortable: false,
								// 	Cell: ({ original }: { original: SinglePaymentHistory }) =>
								// 		original.accountId ? <TextCell text={original.accountId} /> : null,
								// },
								{
									Header: invoiceListLabels.paymentMethod,
									accessor: 'paymentMethod',

									Cell: ({ original }: { original: SinglePaymentHistory }) =>
										original.paymentMethod ? (
											<TextCell text={invoiceListLabels[original.paymentMethod]} />
										) : null,
								},
								{
									Header: translate('amount'),
									accessor: 'amount.value',
									Cell: ({ original }: { original: SinglePaymentHistory }) => (
										<div className={classes.amountCell}>
											<BodyMBold inline>{roundup(original.amount.value)}</BodyMBold>{' '}
											<BodyM inline>{original.amount.unit}</BodyM>
										</div>
									),
								},
								{
									Header: translate('table.download.pdf'),
									id: 'documents',
									sortable: false,
									Cell: ({ original }: { original: SinglePaymentHistory }) => {
										return (
											<div
												id={`${original.paymentRequestId}-download-button`}
												className={classes.document}
												onClick={() =>
													getPaymentReceipt(original.paymentRequestId, original.paymentDate)
												}
											>
												<img src={PDF} alt="pdf" />
											</div>
										);
									},
								},
						  ].filter(Boolean) as Array<any>)
						: featureToggleColumns
				}
				setSearchInputValue={setSearchValue}
				searchInputValue={searchValue}
			/>
			<div className={classes.downloadInvoiceWrapper}>
				<div className={classes.invoiceSelectSection}>
					<BodyMBold customClassName={classes.downloadText} disableMargin>
						{!selectedPayments.length
							? invoiceListLabels.selectInvoices
							: dynamicTranslate(invoiceListLabels.receiptsSelected, selectedPayments.length)}
					</BodyMBold>
				</div>

				<div className={classes.downloadButtonWrapper}>
					<Button
						color="primary-dark"
						size="sm"
						onClick={exportToXls}
						className={classes.downloadButton}
						disabled={!selectedPayments.length}
					>
						{invoiceListLabels.downloadButton}
					</Button>
				</div>
			</div>
			<BreadcrumbsItem to="/payments/payments-receipts">
				{`${translate('path.my.payments')} - ${translate('path.my.payments.receipts')}`}
			</BreadcrumbsItem>
		</div>
	);
};
export default PaymentsHistory;
